import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  PawPrint, Gem, Sparkles, Loader2, Plus, Minus, Edit3,
  Heart, Shield, Zap, Store, Image as ImageIcon,
} from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { themeOf, ARENA, PET_SLOT_THEME, rarityOf } from '../lib/classTheme';
import {
  ArenaFloor, Pedestal, ArenaTitle, ArenaTabs, ArenaStatBar,
  GoldFrame, Laurel, ArenaEmpty, RarityBadge,
} from '../components/ArenaFrame';
import { Sheet, SkeletonList } from '../components/ui';

const PET_STAT_COLORS = {
  power: '#c0392b', agility: '#27ae60', vitality: '#c0392b',
  loyalty: '#c9973a', instinct: '#8e44ad', spirit: '#16a085',
};

export default function PetPage() {
  const qc = useQueryClient();
  const { user, load } = useAuth();
  const [tab, setTab] = useState('stats');
  const [alloc, setAlloc] = useState({});
  const [buyAlloc, setBuyAlloc] = useState({});
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [sceneBusy, setSceneBusy] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['pet'], queryFn: () => api.get('/pets').then(r => r.data) });
  const { data: meta } = useQuery({ queryKey: ['petMeta'], queryFn: () => api.get('/pets/meta').then(r => r.data) });
  const { data: inv } = useQuery({
    queryKey: ['petInventory'], queryFn: () => api.get('/pets/inventory').then(r => r.data),
    enabled: tab === 'gear',
  });

  const refresh = () => { qc.invalidateQueries(['pet', 'petInventory', 'hero']); load(); };

  const allocM = useMutation({
    mutationFn: () => api.post('/pets/allocate', { allocation: alloc }),
    onSuccess: () => { toast.success('Companion trained! 🐾'); setAlloc({}); refresh(); },
    onError: e => toast.error(e.message),
  });

  const buyM = useMutation({
    mutationFn: () => api.post('/pets/buy-stats', { allocation: buyAlloc }),
    onSuccess: ({ data: d }) => { toast.success(`+${d.statsAdded} stats · −💎${d.spent}`); setBuyAlloc({}); refresh(); },
    onError: e => toast.error(e.message),
  });

  const equipM = useMutation({
    mutationFn: (id) => api.post(`/pets/equip/${id}`),
    onSuccess: () => { toast.success('Equipped!'); refresh(); },
    onError: e => toast.error(e.message),
  });

  const unequipM = useMutation({
    mutationFn: (slot) => api.post(`/pets/unequip/${slot}`),
    onSuccess: () => { toast('Removed'); refresh(); },
  });

  const renameM = useMutation({
    mutationFn: (name) => api.post('/pets/rename', { name }),
    onSuccess: () => { toast.success('Renamed!'); refresh(); },
  });

  async function makeScene() {
    setSceneBusy(true);
    try {
      const { data: d } = await api.post('/pets/scene', { rarity: 'epic' });
      toast.success(d.cached ? 'Portrait ready!' : 'Portrait queued — check back in a minute');
      refresh();
    } catch (e) { toast.error(e.message); } finally { setSceneBusy(false); }
  }

  const totalAlloc = Object.values(alloc).reduce((a, b) => a + (b || 0), 0);
  const totalBuy = Object.values(buyAlloc).reduce((a, b) => a + (b || 0), 0);
  const buyCost = totalBuy * (meta?.statCost || 2);

  if (isLoading || !meta) return <SkeletonList n={4} h="h-40" />;

  const pet = data?.pet;
  const t = themeOf(data?.classKey);

  /* ═══════ NO PET ═══════ */
  if (!pet) {
    return (
      <div className="space-y-4 animate-fade-up">
        <div className="card-arena arena-arches p-6 text-center">
          <ArenaFloor classKey={data?.classKey} className="p-6 -m-6 rounded-2xl" showCrowd={false}>
            <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity }}>
              <PawPrint size={48} className="mx-auto mb-3" style={{ color: ARENA.goldL }} />
            </motion.div>
            <h1 className="font-display text-2xl font-extrabold tracking-wide"
              style={{ color: ARENA.goldL }}>Companion</h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(232,220,200,.7)' }}>
              A loyal beast fights beside you in the arena
            </p>
            <button onClick={() => setAdoptOpen(true)} className="btn-primary mt-4">
              <Sparkles size={15} /> Choose your companion
            </button>
            <p className="text-[11px] mt-2 font-semibold" style={{ color: 'rgba(232,220,200,.5)' }}>
              First one is <b style={{ color: ARENA.goldL }}>free</b>
            </p>
          </ArenaFloor>
        </div>

        <AdoptSheet open={adoptOpen} onClose={() => setAdoptOpen(false)}
          classKey={data?.classKey} onSuccess={refresh} />
      </div>
    );
  }

  /* ═══════ HAS PET ═══════ */
  return (
    <div className="space-y-4 animate-fade-up">
      {/* ═══ HEADER ═══ */}
      <div className="card-arena arena-arches overflow-hidden">
        <ArenaFloor classKey={data.classKey} className="p-5" showCrowd={false}>
          <div className="flex gap-4">
            <Pedestal glow={t.hex}>
              <motion.div className="w-28 h-36 rounded-xl overflow-hidden"
                style={{ padding: 2.5, background: ARENA.goldFrame, boxShadow: t.glowCss }}
                animate={{ y: [0, -5, 0], rotate: [0, 1.5, -1.5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}>
                <div className="w-full h-full rounded-[10px] overflow-hidden bg-stone-900">
                  <img src={pet.sceneUrl || pet.image} alt={pet.name}
                    className="w-full h-full object-cover object-top" />
                </div>
              </motion.div>
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full
                font-display font-black text-[10px] text-white z-20"
                style={{ background: ARENA.goldFrame }}>
                LVL {pet.level}
              </div>
            </Pedestal>

            <div className="flex-1 min-w-0 text-white">
              <div className="flex items-center gap-1.5 mb-1">
                <Laurel size={20} color={ARENA.goldL} />
                <span className="text-[9px] font-display font-black uppercase tracking-[0.2em]"
                  style={{ color: ARENA.goldL }}>{pet.species}</span>
              </div>

              <button onClick={() => { const n = prompt('Companion name', pet.name); if (n) renameM.mutate(n); }}
                className="flex items-center gap-1.5 group">
                <h1 className="font-display text-xl font-extrabold tracking-wide truncate"
                  style={{ color: ARENA.goldL, textShadow: '0 2px 14px rgba(201,151,58,.45)' }}>
                  {pet.name}
                </h1>
                <Edit3 size={12} className="opacity-0 group-hover:opacity-60 transition"
                  style={{ color: ARENA.goldL }} />
              </button>

              <p className="text-[11px] font-semibold italic" style={{ color: 'rgba(232,220,200,.6)' }}>
                {pet.tagline}
              </p>

              <div className="mt-2.5">
                <div className="flex justify-between text-[10px] font-display font-bold mb-1"
                  style={{ color: ARENA.goldL }}>
                  <span>XP {compact(pet.xpProgress.current)} / {compact(pet.xpProgress.needed)}</span>
                </div>
                <ArenaStatBar value={pet.xpProgress.current} max={pet.xpProgress.needed} height={6} />
              </div>

              <div className="grid grid-cols-3 gap-1.5 mt-2.5">
                <Mini l="POWER"   v={pet.power} />
                <Mini l="GEAR"    v={pet.gearScore} />
                <Mini l="ASSISTS" v={pet.assists} />
              </div>
            </div>
          </div>
        </ArenaFloor>

        <div className="p-3 grid grid-cols-3 gap-2" style={{ background: 'var(--sand-100)' }}>
          <button onClick={makeScene} disabled={sceneBusy} className="btn-ghost btn-sm flex-col !py-2.5 h-auto">
            {sceneBusy ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} />}
            <span className="text-[10px] font-display">Portrait</span>
          </button>
          <Link to="/pet-shop" className="btn-primary btn-sm flex-col !py-2.5 h-auto">
            <Store size={15} /><span className="text-[10px] font-display">Gear shop</span>
          </Link>
          <button onClick={() => setAdoptOpen(true)} className="btn-ghost btn-sm flex-col !py-2.5 h-auto">
            <PawPrint size={15} /><span className="text-[10px] font-display">Companions</span>
          </button>
        </div>
      </div>

      {/* ═══ COMBAT INFO ═══ */}
      <div className="card p-3.5">
        <ArenaTitle title="Battle assistance" latin="Auxilium" />
        <div className="grid grid-cols-3 gap-2">
          <Assist icon={Zap}    label="Assist" pct={Math.round((meta.combat.assistChance + pet.stats.agility * 0.006) * 100)}
            color="#c0392b" desc="Extra damage" />
          <Assist icon={Shield} label="Guard"  pct={Math.round((meta.combat.guardChance + pet.stats.loyalty * 0.005) * 100)}
            color="#c9973a" desc="Blocks damage" />
          <Assist icon={Heart}  label="Heal"   pct={Math.round((meta.combat.healChance + pet.stats.spirit * 0.004) * 100)}
            color="#16a085" desc="When low HP" />
        </div>
      </div>

      {/* ═══ TABS ═══ */}
      <ArenaTabs value={tab} onChange={setTab} tabs={[
        { key: 'stats', label: 'Traits', icon: '⚡' },
        { key: 'gear',  label: 'Gear', icon: '👑' },
      ]} />

      {tab === 'stats' && (
        <>
          {pet.statPoints > 0 && (
            <div className="card p-4" style={{ borderColor: ARENA.gold, borderWidth: 2 }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles size={17} style={{ color: ARENA.gold }} />
                  <span className="font-display font-extrabold text-sm">
                    {pet.statPoints - totalAlloc} free training points
                  </span>
                </div>
                {totalAlloc > 0 && (
                  <button onClick={() => allocM.mutate()} disabled={allocM.isPending}
                    className="btn-primary btn-sm">Train {totalAlloc}</button>
                )}
              </div>
            </div>
          )}

          <div className="card p-4" style={{ borderColor: '#34d399', borderWidth: 2 }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gem size={17} className="text-mint-600" />
                <span className="font-display font-extrabold text-sm">
                  {meta.statCost} points = 1 trait
                </span>
              </div>
              {totalBuy > 0 && (
                <button onClick={() => buyM.mutate()}
                  disabled={buyM.isPending || buyCost > user.points_balance}
                  className="btn-mint btn-sm">Buy {totalBuy} · 💎{compact(buyCost)}</button>
              )}
            </div>
          </div>

          <div className="card p-4 space-y-2.5">
            {meta.statKeys.map(k => {
              const m = meta.stats[k];
              const base = pet.baseStats[k];
              const eq = pet.equipmentStats[k] || 0;
              const pending = (alloc[k] || 0) + (buyAlloc[k] || 0);
              const total = base + eq + pending;

              return (
                <div key={k} className="flex items-center gap-3">
                  <span className="text-base w-6 text-center shrink-0">{m.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm font-display font-bold">{m.label}</span>
                      <span className="text-sm font-display font-black tabular-nums">
                        {base}
                        {eq > 0 && <span className="text-mint-600 text-xs"> +{eq}</span>}
                        {pending > 0 && <span className="text-xs" style={{ color: ARENA.goldD }}> +{pending}</span>}
                      </span>
                    </div>
                    <div className="mt-1">
                      <ArenaStatBar value={total} max={100} height={7}
                        color={`linear-gradient(90deg, ${PET_STAT_COLORS[k]}, ${PET_STAT_COLORS[k]}cc)`} />
                    </div>
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--stone-400)' }}>{m.desc}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {pet.statPoints - totalAlloc > 0 && (
                      <button onClick={() => setAlloc({ ...alloc, [k]: (alloc[k] || 0) + 1 })}
                        className="w-7 h-7 rounded-lg grid place-items-center text-white active:scale-90"
                        style={{ background: ARENA.goldFrame }}><Plus size={13} strokeWidth={3} /></button>
                    )}
                    {(alloc[k] > 0 || buyAlloc[k] > 0) && (
                      <button onClick={() => {
                        if (alloc[k] > 0) setAlloc({ ...alloc, [k]: alloc[k] - 1 });
                        else setBuyAlloc({ ...buyAlloc, [k]: buyAlloc[k] - 1 });
                      }} className="w-7 h-7 rounded-lg grid place-items-center"
                        style={{ background: 'var(--stone-200)', color: 'var(--stone-600)' }}>
                        <Minus size={13} strokeWidth={3} /></button>
                    )}
                    <button onClick={() => setBuyAlloc({ ...buyAlloc, [k]: (buyAlloc[k] || 0) + 1 })}
                      className="w-7 h-7 rounded-lg grid place-items-center text-white bg-mint-500 active:scale-90">
                      <Gem size={11} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === 'gear' && (
        <>
          <div className="card overflow-hidden">
            <ArenaFloor classKey={data.classKey} className="p-4" showCrowd={false}>
              <div className="flex items-center justify-center gap-3">
                {Object.keys(PET_SLOT_THEME).map(s => (
                  <PetSlot key={s} slot={s} item={pet.equipment?.[s]}
                    onUnequip={() => unequipM.mutate(s)} />
                ))}
              </div>
              <p className="text-center text-[10px] font-display font-bold mt-3"
                style={{ color: ARENA.goldL }}>
                {pet.gearScore} GEAR SCORE · {pet.equippedCount}/3 SLOTS
              </p>
            </ArenaFloor>
          </div>

          <ArenaTitle title="Companion gear" latin="Ornamenta"
            right={<Link to="/pet-shop" className="btn-primary btn-sm"><Store size={12} /> Shop</Link>} />

          {!inv?.length
            ? <ArenaEmpty emoji="👑" title="No gear yet"
                sub="Equip your companion to boost its battle assistance."
                action={<Link to="/pet-shop" className="btn-primary btn-sm">Visit gear shop</Link>} />
            : <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                {inv.map(it => (
                  <PetItemCard key={it.id} item={it}
                    onClick={() => !it.equipped && equipM.mutate(it.id)} />
                ))}
              </div>}
        </>
      )}

      <AdoptSheet open={adoptOpen} onClose={() => setAdoptOpen(false)}
        classKey={data.classKey} owned={data.pets} onSuccess={refresh} />
    </div>
  );
}

/* ---------------- SUB-COMPONENTS ---------------- */
function Mini({ l, v }) {
  return (
    <div className="rounded-lg px-2 py-1.5 text-center"
      style={{ background: 'rgba(232,200,116,.14)', border: '1px solid rgba(232,200,116,.22)' }}>
      <p className="text-[8px] font-display font-bold tracking-wider"
        style={{ color: 'rgba(232,220,200,.6)' }}>{l}</p>
      <p className="font-display font-black text-sm" style={{ color: ARENA.goldL }}>{v}</p>
    </div>
  );
}

function Assist({ icon: Icon, label, pct, color, desc }) {
  return (
    <div className="rounded-xl p-2.5 text-center" style={{ background: 'var(--sand-100)' }}>
      <Icon size={16} className="mx-auto mb-1" style={{ color }} />
      <p className="font-display font-black text-base" style={{ color }}>{pct}%</p>
      <p className="text-[9px] font-display font-bold" style={{ color: 'var(--stone-600)' }}>{label}</p>
      <p className="text-[8px]" style={{ color: 'var(--stone-400)' }}>{desc}</p>
    </div>
  );
}

function PetSlot({ slot, item, onUnequip }) {
  const s = PET_SLOT_THEME[slot];
  const r = item ? rarityOf(item.rarity) : null;
  return (
    <div className="relative">
      <div className="w-[62px] h-[62px] rounded-xl overflow-hidden"
        style={item
          ? { padding: 2, background: `linear-gradient(135deg,${r.hex},${r.glow})`, boxShadow: r.shadowCss }
          : { border: '2px dashed rgba(232,200,116,.3)' }}>
        {item ? (
          <div className="w-full h-full rounded-lg overflow-hidden bg-stone-900">
            <img src={item.thumb || item.image} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-full h-full grid place-items-center text-xl opacity-30">{s.icon}</div>
        )}
      </div>
      {item && onUnequip && (
        <button onClick={onUnequip}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-white text-xs
            grid place-items-center shadow-lg"
          style={{ background: ARENA.blood }}>✕</button>
      )}
      <p className="text-[7px] font-display font-black text-center mt-0.5 tracking-wider"
        style={{ color: 'rgba(232,220,200,.55)' }}>{s.latin}</p>
    </div>
  );
}

function PetItemCard({ item, onClick }) {
  const r = rarityOf(item.rarity);
  const s = PET_SLOT_THEME[item.slot];
  return (
    <motion.button onClick={onClick} whileTap={{ scale: .95 }}
      className="relative rounded-xl overflow-hidden text-left"
      style={{ padding: 2, background: `linear-gradient(135deg,${r.hex}55,${r.glow}55)` }}>
      <div className="rounded-[10px] overflow-hidden" style={{ background: 'var(--marble)' }}>
        <div className="relative aspect-square" style={{ background: 'var(--stone-100)' }}>
          {item.thumb || item.image
            ? <img src={item.thumb || item.image} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full grid place-items-center text-2xl opacity-30">{s.icon}</div>}
          {item.equipped && (
            <span className="absolute top-1 left-1 chip-mint !text-[7px]">EQUIPPED</span>
          )}
          {r.tier >= 5 && (
            <motion.div className="absolute inset-0 pointer-events-none"
              style={{ background: 'linear-gradient(115deg,transparent 35%,rgba(255,255,255,.4) 50%,transparent 65%)' }}
              animate={{ x: ['-120%', '120%'] }}
              transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 2.5 }} />
          )}
        </div>
        <div className="p-1.5">
          <RarityBadge rarity={item.rarity} />
          <p className="font-bold text-[10px] leading-tight line-clamp-2 mt-0.5">{item.name}</p>
          <p className="text-[9px] font-black mt-0.5" style={{ color: r.hex }}>+{item.totalStats}</p>
        </div>
      </div>
    </motion.button>
  );
}

function AdoptSheet({ open, onClose, classKey, owned = [], onSuccess }) {
  const [sel, setSel] = useState(null);
  const [name, setName] = useState('');

  const { data } = useQuery({
    queryKey: ['petTemplates', classKey],
    queryFn: () => api.get(`/pets/templates/${classKey}`).then(r => r.data),
    enabled: open && !!classKey,
  });

  const adopt = useMutation({
    mutationFn: () => api.post('/pets/adopt', { templateId: sel, name: name || undefined }),
    onSuccess: ({ data: d }) => {
      toast.success(d.free ? `${d.name} joined you — free! 🐾` : `${d.name} adopted · −💎${d.price}`);
      onClose(); onSuccess();
    },
    onError: e => toast.error(e.message),
  });

  const switchM = useMutation({
    mutationFn: (id) => api.post(`/pets/switch/${id}`),
    onSuccess: () => { toast.success('Companion summoned'); onClose(); onSuccess(); },
  });

  const t = themeOf(classKey);
  const ownedIds = new Set(owned.map(p => p.templateId));

  return (
    <Sheet open={open} onClose={onClose} title="Choose your companion" wide>
      {owned.length > 0 && (
        <div className="mb-4">
          <p className="label">Your companions</p>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {owned.map(p => (
              <button key={p.id} onClick={() => !p.active && switchM.mutate(p.id)}
                className="shrink-0 w-[74px] rounded-xl overflow-hidden transition"
                style={{ padding: 2, background: p.active ? ARENA.goldFrame : 'var(--stone-200)' }}>
                <div className="rounded-lg overflow-hidden bg-stone-200" style={{ aspectRatio: '9/12' }}>
                  <img src={p.thumb || p.image} alt="" className="w-full h-full object-cover" />
                </div>
                <p className="text-[8px] font-display font-bold truncate px-1 py-0.5">{p.name}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="label">Available companions</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {data?.variants?.map(v => {
          const isOwned = ownedIds.has(v.id);
          return (
            <button key={v.id} onClick={() => !isOwned && setSel(v.id)} disabled={isOwned}
              className={`relative rounded-xl overflow-hidden text-left transition
                ${isOwned ? 'opacity-40' : sel === v.id ? 'scale-[1.03]' : 'hover:scale-[1.01]'}`}
              style={{
                padding: 2,
                background: sel === v.id ? ARENA.goldFrame : 'var(--stone-200)',
                boxShadow: sel === v.id ? t.glowCss : 'none',
              }}>
              <div className="rounded-lg overflow-hidden bg-stone-200" style={{ aspectRatio: '9/13' }}>
                <img src={v.thumb || v.image} alt="" className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-1.5"
                style={{ background: 'linear-gradient(180deg,transparent,rgba(36,30,22,.9))' }}>
                <p className="text-[10px] font-display font-black text-white truncate">{v.name}</p>
                <p className="text-[8px] text-white/70 capitalize">{v.vibe} · {v.species}</p>
              </div>
              {isOwned && <span className="absolute top-1.5 left-1.5 chip-mint !text-[7px]">OWNED</span>}
            </button>
          );
        })}
      </div>

      {sel && (
        <>
          <div className="mt-3">
            <label className="label">Name (optional)</label>
            <input className="input" value={name} maxLength={40}
              placeholder={data?.variants?.find(v => v.id === sel)?.name}
              onChange={e => setName(e.target.value)} />
          </div>
          <button onClick={() => adopt.mutate()} disabled={adopt.isPending}
            className="btn-primary w-full mt-3 py-3">
            {adopt.isPending ? <><Loader2 size={16} className="animate-spin" /> Bonding…</>
              : owned.length === 0 ? <><Sparkles size={16} /> Adopt — FREE</>
              : <>Adopt · 💎{compact(2500)}</>}
          </button>
        </>
      )}
    </Sheet>
  );
}